import {useReducer,useCallback} from 'react';
import {nanoid} from 'nanoid';

const DEFAULT_WELCOME={role:'model',content:'Hello! How can I help with your flowchart?'}
const newDefaultTab=()=>({id:nanoid(),name:'Untitled',messages:[DEFAULT_WELCOME]})

function tabsReducer(state,action) {
    const {tabs,activeTabId}=state
    switch(action.type) {
        case 'ADD_TAB': {
            const tab=action.tab
            return {tabs:[...tabs,tab],activeTabId:tab.id}
        }
        case 'REMOVE_TAB': {
            const id=action.tabId
            let next=tabs.filter(t=>t.id!==id)
            if(next.length===0) next=[newDefaultTab()]
            // If removed tab was active, switch to the tab before it (or first)
            let newActive=activeTabId
            if(activeTabId===id) {
                const removedIdx=tabs.findIndex(t=>t.id===id)
                const fallbackIdx=Math.max(0,removedIdx-1)
                newActive=next[Math.min(fallbackIdx,next.length-1)].id
            }
            return {tabs:next,activeTabId:newActive}
        }
        case 'SET_ACTIVE': return {...state,activeTabId:action.tabId}
        case 'UPDATE_MESSAGES': return {
            ...state,
            tabs:tabs.map(t=>t.id===action.tabId?{...t,messages:action.messages}:t)
        }
        case 'RENAME': return {
            ...state,
            tabs:tabs.map(t=>t.id===action.tabId?{...t,name:action.name}:t)
        }
        case 'CLEAR': return {
            ...state,
            tabs:tabs.map(t=>t.id===action.tabId?{...t,name:'Untitled',messages:[DEFAULT_WELCOME]}:t)
        }
        case 'DUPLICATE': {
            const src=tabs.find(t=>t.id===action.tabId)
            if(!src) return state
            const copy={id:nanoid(),name:`${src.name} (Copy)`,messages:[...src.messages]}
            return {tabs:[...tabs,copy],activeTabId:copy.id}
        }
        case 'LOAD': {
            const {savedTabs,legacyChats}=action
            if(Array.isArray(savedTabs)&&savedTabs.length>0) {
                return {tabs:savedTabs,activeTabId:savedTabs[0].id}
            }
            if(Array.isArray(legacyChats)&&legacyChats.length>0) {
                const t={id:nanoid(),name:'Chat',messages:legacyChats}
                return {tabs:[t],activeTabId:t.id}
            }
            return state
        }
        case 'SET_TABS': return {...state,tabs:action.tabs}
        default: return state
    }
}

/**
 * useChatTabs — manages multiple chat sessions with tabs.
 * Uses a reducer so that tab removal + active-tab switching are a single atomic update,
 * eliminating the double-setTabs / stale-state bug.
 */
export default function useChatTabs(initialTabs=null) {
    const initialState=initialTabs
        ?{tabs:initialTabs,activeTabId:initialTabs[0].id}
        :(()=>{const t=newDefaultTab();return{tabs:[t],activeTabId:t.id}})()

    const [state,dispatch]=useReducer(tabsReducer,initialState)
    const {tabs,activeTabId}=state
    const activeTab=tabs.find(t=>t.id===activeTabId)||tabs[0]

    const addTab=useCallback((tab)=>dispatch({
        type:'ADD_TAB',
        tab:{id:tab.id||nanoid(),name:tab.name||'Untitled',messages:tab.messages||[DEFAULT_WELCOME]}
    }),[])

    // Single dispatch — atomically removes tab AND picks new active tab (6.5.4 + 6.5.5)
    const removeTab=useCallback((tabId)=>dispatch({type:'REMOVE_TAB',tabId}),[])

    const setActiveTabId=useCallback((tabId)=>dispatch({type:'SET_ACTIVE',tabId}),[])

    const updateTabMessages=useCallback((tabId,messages)=>
        dispatch({type:'UPDATE_MESSAGES',tabId,messages}),[])

    const updateActiveTabMessages=useCallback((messages)=>
        dispatch({type:'UPDATE_MESSAGES',tabId:activeTabId,messages}),[activeTabId])

    const renameTab=useCallback((tabId,name)=>dispatch({type:'RENAME',tabId,name}),[])

    const clearTab=useCallback((tabId)=>dispatch({type:'CLEAR',tabId}),[])

    const duplicateTab=useCallback((tabId)=>dispatch({type:'DUPLICATE',tabId}),[])

    const loadTabs=useCallback((savedTabs,legacyChats=null)=>
        dispatch({type:'LOAD',savedTabs,legacyChats}),[])

    // Expose setTabs for backwards compatibility (Design.js uses it via useProject)
    const setTabs=useCallback((updater)=>{
        const next=typeof updater==='function'?updater(tabs):updater
        dispatch({type:'SET_TABS',tabs:next})
    },[tabs])

    return {
        tabs,activeTabId,activeTab,
        setTabs,setActiveTabId,
        addTab,removeTab,
        updateTabMessages,updateActiveTabMessages,
        renameTab,clearTab,duplicateTab,loadTabs
    }
}
